<template>
    <div class="container-fluid">
        <!-- Filter and Actions Toolbar -->
        <div class="opportunity-filters-card mb-3">
            <div class="filters-header">
                <div class="d-flex align-items-center gap-2">
                    <div class="filter-header-icon">
                        <i class="material-icons">filter_alt</i>
                    </div>
                    <div>
                        <h6 class="filter-title mb-0">Find an Opportunity</h6>
                        <p class="filter-subtitle mb-0">Filter by skills and activities</p>
                    </div>
                </div>

                <div class="filters-actions">
                    <button v-if="hasFilters" type="button" class="btn-clear-filters" @click="clearFilters">
                        <i class="material-icons text-sm align-middle me-1">filter_alt_off</i> Clear filters
                    </button>

                    <div v-if="hasNonSignupRows" class="signup-toggle-wrapper">
                        <label class="toggle-switch me-2" :title="showOnlySignup ? 'Showing sign-up only' : 'Showing all'">
                            <input type="checkbox" v-model="showOnlySignup" />
                            <span class="toggle-slider"></span>
                        </label>
                        <span class="toggle-label">{{ showOnlySignup ? 'Sign-up only' : 'All positions' }}</span>
                    </div>

                    <button class="btn-refresh" @click="refreshTable()">
                        <i class="material-icons text-sm align-middle">refresh</i> Refresh
                    </button>
                </div>
            </div>

            <!-- Filters Grid -->
            <div class="filters-grid mt-3">
                <div class="filter-col">
                    <label class="filter-window-label" for="opportunity-window">Show positions through</label>
                    <select id="opportunity-window" v-model="futureDays" class="form-select">
                        <option v-for="days in futureDayOptions" :key="days" :value="days">Next {{ days }} days</option>
                    </select>
                </div>

                <div class="filter-col">
                    <MaterialMultiSelect
                        id="opportunity-skill-filter"
                        label="Skills"
                        placeholder="All skills"
                        icon="psychology"
                        v-model="selectedSkillIds"
                        :items="skillOptions"
                        itemTrackBy="id"
                        itemLabel="name"
                        emitAs="id"
                        :allowSelectAll="true"
                        :showSelectedOptions="false"
                        marginClass="mb-0"
                    />
                </div>

                <div class="filter-col">
                    <MaterialMultiSelect
                        id="opportunity-activity-filter"
                        label="Activities"
                        placeholder="All activities"
                        icon="event"
                        v-model="selectedActivityIds"
                        :items="activityOptions"
                        itemTrackBy="id"
                        itemLabel="name"
                        emitAs="id"
                        :allowSelectAll="true"
                        :showSelectedOptions="false"
                        marginClass="mb-0"
                    />
                </div>
            </div>

            <!-- Active Filter Chips Bar -->
            <div v-if="hasFilters" class="active-filters-bar mt-2 pt-2">
                <div class="d-flex align-items-center flex-wrap gap-1">
                    <span class="text-xs text-muted me-1 font-weight-bold">Active:</span>

                    <span
                        v-for="skill in selectedSkillObjects"
                        :key="`active-skill-${skill.id}`"
                        class="active-filter-chip active-filter-chip--skill"
                    >
                        <i class="material-icons text-xs me-1">psychology</i>
                        <span class="chip-text">{{ skill.name }}</span>
                        <button type="button" class="chip-remove" aria-label="Remove skill filter" @click="removeSkillFilter(skill.id)">
                            <i class="material-icons text-xs">close</i>
                        </button>
                    </span>

                    <span
                        v-for="activity in selectedActivityObjects"
                        :key="`active-act-${activity.id}`"
                        class="active-filter-chip active-filter-chip--activity"
                    >
                        <i class="material-icons text-xs me-1">event</i>
                        <span class="chip-text">{{ activity.name }}</span>
                        <button type="button" class="chip-remove" aria-label="Remove activity filter" @click="removeActivityFilter(activity.id)">
                            <i class="material-icons text-xs">close</i>
                        </button>
                    </span>

                    <button type="button" class="btn-reset-link ms-2" @click="clearFilters">
                        Reset all
                    </button>
                </div>
            </div>
        </div>

        <!-- Table -->
        <Table heading="Open positions that need volunteers" :rows="filteredPositions" :columns="columns"
            :selectable="false" @update:selected="selected = $event" :loading="loading" :pagination="pagination"
            @changePage="onChangePage" @updatePageSize="onUpdatePageSize" @searchChanged="onSearchChanged">
            <template #col-workgroup_name="{ row }">
                <p class="mb-0">
                    {{ row.workgroup_name }}
                    <small class="d-block text-muted"><i>Positions Available: {{ row?.openings_available }}</i></small>
                </p>
            </template>

            <template #col-activity_name="{ row }">
                <p class="mb-0">{{ row.activity_name }}<small v-if="row.skill_name" class="d-block text-muted">Skill: {{ row.skill_name }}</small></p>
            </template>

            <template #actions="{ row }">
                <button v-if="row.can_sign_up" @click="confirmRegistration(row)"
                    :disabled="savingRowId === row.aiwg_id" class="btn btn-outline-info btn-sm me-1 my-0">
                    <span v-if="savingRowId === row.aiwg_id" class="spinner-border spinner-border-sm me-2"
                        role="status" aria-hidden="true"></span>
                    <i v-else class="material-icons text-lg me-1">save</i>
                    {{ savingRowId === row.aiwg_id ? 'Signing Up...' : 'Sign Up' }}
                </button>
                <span v-else class="availability-note">
                    <i class="material-icons text-sm align-middle me-1">info</i>
                    You are unavailable or do not meet the requirements for this position.
                </span>
            </template>
        </Table>

        <!-- Current Skills Card -->
        <div class="skills-card mt-4">
            <div class="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <div>
                    <h5 class="mb-1">My current skills</h5>
                    <p class="text-muted mb-0">Approved skills used to match you with serving opportunities.</p>
                </div>
                <button class="btn btn-outline-primary btn-sm" @click="$emit('open-interests')">
                    <i class="material-icons text-sm align-middle me-1">add</i> Request another area
                </button>
            </div>
            <div v-if="userSkills.length" class="skill-list mt-3">
                <span v-for="skill in userSkills" :key="skill.skillid" class="skill-badge approved-skill">{{ skill.skillname }}</span>
            </div>
            <p v-else class="text-muted mt-3 mb-0">No approved skills are assigned yet.</p>
            <div v-if="userInterestSkills.length" class="pending-section mt-3">
                <strong><i class="material-icons text-sm align-middle me-1">schedule</i> Pending interest requests</strong>
                <div class="skill-list mt-2">
                    <span v-for="skill in userInterestSkills" :key="skill.skillid" class="skill-badge pending-skill">{{ skill.skillname }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import Table from "@/components/Table/Table.vue";
import MaterialMultiSelect from "@/components/common/MaterialMultiSelect.vue";
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";

const props = defineProps({
    locationID: {
        type: String,
        default: null
    },
    userID: {
        type: String,
        default: null
    }
});
defineEmits(["open-interests"]);

const store = useStore();
const route = useRoute();
const toast = useToast();

const selected = ref([]);
const loading = ref(true);
const savingRowId = ref(null);
const showOnlySignup = ref(false);
const selectedSkillIds = ref([]);
const selectedActivityIds = ref([]);
const futureDayOptions = [30, 45, 60, 75, 90, 120];
const futureDays = ref(60);

const locationId = props.locationID || route.params.locationID;
const personId = props.userID || route.params.userID;
const searchTerm = ref("");

const pagination = computed(() => store.getters.signupFormPagination);

const columns = [
    { key: "date", label: "Date" },
    { key: "activity_name", label: "Activity/Event" },
    { key: "workgroup_name", label: "Workgroup" },
];

// 🧠 Vuex data
const allOpenPositions = computed(() => store.getters.allOpenPositions);
const userSkills = computed(() => store.getters.userSkills || []);
const userInterestSkills = computed(() => store.getters.userInterestSkills || []);
const skillOptions = computed(() => (store.getters.signupSkillOptions || []).map(skill => ({
    id: String(skill.skillid),
    name: skill.skillname,
})));
const activityOptions = computed(() => (store.getters.signupActivityOptions || []).map(activity => ({
    id: String(activity.activityid),
    name: activity.activityname,
})));

const hasFilters = computed(() => selectedSkillIds.value.length > 0 || selectedActivityIds.value.length > 0);

const selectedSkillObjects = computed(() =>
    skillOptions.value.filter(skill => selectedSkillIds.value.map(String).includes(String(skill.id)))
);

const selectedActivityObjects = computed(() =>
    activityOptions.value.filter(activity => selectedActivityIds.value.map(String).includes(String(activity.id)))
);

const hasNonSignupRows = computed(() =>
    allOpenPositions.value.some(row => !row.can_sign_up)
);

const filteredPositions = computed(() => {
    return allOpenPositions.value.filter(row =>
        (!showOnlySignup.value || row.can_sign_up) &&
        (!selectedSkillIds.value.length || selectedSkillIds.value.map(String).includes(String(row.skill_id))) &&
        (!selectedActivityIds.value.length || selectedActivityIds.value.map(String).includes(String(row.activity_id)))
    );
});

// ✅ Fetch paginated positions
const fetchOpenPositions = async (page = 1, pageSize = pagination.value.pageSize || 10) => {
    loading.value = true;
    try {
        await store.dispatch("getOpenPositions", {
            locationId,
            personId,
            search: searchTerm.value,
            skillIds: selectedSkillIds.value,
            activityIds: selectedActivityIds.value,
            futureDays: futureDays.value,
            page,
            pageSize,
        });
    } finally {
        loading.value = false;
    }
};

onMounted(() => fetchOpenPositions());

watch([selectedSkillIds, selectedActivityIds, futureDays], () => fetchOpenPositions(1));

const removeSkillFilter = (id) => {
    selectedSkillIds.value = selectedSkillIds.value.filter(sId => String(sId) !== String(id));
};

const removeActivityFilter = (id) => {
    selectedActivityIds.value = selectedActivityIds.value.filter(aId => String(aId) !== String(id));
};

const clearFilters = () => {
    selectedSkillIds.value = [];
    selectedActivityIds.value = [];
};

// ✅ Pagination handlers
const onChangePage = async (page) => {
    await fetchOpenPositions(page);
};

const onUpdatePageSize = async (size) => {
    await fetchOpenPositions(1, size);
};

const onSearchChanged = async (term) => {
    searchTerm.value = term;
    await fetchOpenPositions(1);
};

const refreshTable = () => {
    fetchOpenPositions(pagination.value.currentPage || 1);
};

const confirmRegistration = async (row) => {
    savingRowId.value = row.aiwg_id; // Mark this row as saving
    try {
        await store.dispatch("signupForWorkgroup", {
            locationId,
            personId,
            aiwg: row.aiwg_id,
        });
        await fetchOpenPositions(); // Refresh after successful signup
    } catch (error) {
        toast.error(error?.message || "You are no longer eligible for this position.", { position: "top-right" });
    } finally {
        savingRowId.value = null; // Re-enable button after completion
    }
};
</script>

<style scoped>
.opportunity-filters-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.filters-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}

.filter-header-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    color: #4f46e5;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.filter-header-icon i {
    font-size: 20px;
}

.filter-title {
    color: #1e293b;
    font-size: 0.95rem;
    font-weight: 700;
}

.filter-subtitle {
    color: #64748b;
    font-size: 0.78rem;
}

.filters-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.signup-toggle-wrapper {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
}

.toggle-switch {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    flex-shrink: 0;
    margin-bottom: 0;
}

.toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background: #cbd5e1;
    border-radius: 20px;
    transition: all 0.25s ease;
}

.toggle-slider::before {
    content: "";
    position: absolute;
    height: 14px;
    width: 14px;
    left: 3px;
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: all 0.25s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
    background: linear-gradient(135deg, #667eea, #764ba2);
}

.toggle-switch input:checked + .toggle-slider::before {
    transform: translateX(16px);
}

.toggle-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #475569;
    user-select: none;
}

.btn-refresh {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    border: none;
    border-radius: 20px;
    padding: 6px 16px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(102, 126, 234, 0.35);
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.btn-refresh:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.45);
    color: #fff;
}

.btn-refresh:active {
    transform: translateY(0);
}

.availability-note {
    display: inline-flex;
    align-items: center;
    color: #92400e;
    font-size: 0.75rem;
    line-height: 1.35;
    max-width: 230px;
}

.btn-clear-filters {
    padding: 4px 10px;
    border: 1px solid #fecaca;
    border-radius: 20px;
    background: #fff5f5;
    color: #dc2626;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;
}

.btn-clear-filters:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #b91c1c;
}

.filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
}

.filter-col {
    min-width: 0;
}

.active-filters-bar {
    border-top: 1px solid #f1f5f9;
}

.active-filter-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 16px;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.4;
    transition: all 0.15s ease;
}

.active-filter-chip--skill {
    background: #eef2ff;
    color: #4338ca;
    border: 1px solid #c7d2fe;
}

.active-filter-chip--activity {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
}

.chip-text {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chip-remove {
    background: transparent;
    border: none;
    padding: 0;
    margin-left: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    color: inherit;
    opacity: 0.7;
    transition: opacity 0.15s;
}

.chip-remove:hover {
    opacity: 1;
}

.btn-reset-link {
    background: none;
    border: none;
    padding: 0;
    color: #dc2626;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
}

.btn-reset-link:hover {
    color: #991b1b;
}

.skills-card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.25rem;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.skill-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.skill-badge {
    border-radius: 999px;
    padding: 0.35rem 0.75rem;
    font-size: 0.82rem;
    font-weight: 600;
}

.approved-skill {
    color: #166534;
    background: #dcfce7;
    border: 1px solid #bbf7d0;
}

.pending-section {
    color: #92400e;
}

.pending-skill {
    color: #92400e;
    background: #fef3c7;
    border: 1px solid #fde68a;
}
</style>

